import React, { useState, useEffect, useCallback } from 'react';
import './UserManagement.css';
import { api, useLocalStorageFallback } from '../services/api';

function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    isAdmin: false
  });

  const useFallback = useLocalStorageFallback();

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');

    if (useFallback) {
      // Use localStorage
      const savedUsers = localStorage.getItem('zenio_users');
      if (savedUsers) {
        setUsers(JSON.parse(savedUsers));
      } else {
        // Initialize with default users
        const defaultUsers = [
          { id: 1, username: 'admin', password: 'admin123', isAdmin: true, name: 'Admin User' },
          { id: 2, username: 'user', password: 'user123', isAdmin: false, name: 'Regular User' },
          { id: 3, username: 'manager', password: 'manager123', isAdmin: true, name: 'Manager' }
        ];
        localStorage.setItem('zenio_users', JSON.stringify(defaultUsers));
        setUsers(defaultUsers);
      }
      setIsLoading(false);
    } else {
      // Use API
      try {
        const result = await api.getUsers();
        if (result.success && result.users) {
          setUsers(result.users);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Failed to load users:', error);
        setError('Failed to load users');
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    }
  }, [useFallback]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const saveUsersLocalStorage = (updatedUsers) => {
    localStorage.setItem('zenio_users', JSON.stringify(updatedUsers));
    setUsers(updatedUsers);
  };

  const handleAddUser = async () => {
    if (!formData.username || !formData.password || !formData.name) {
      return;
    }

    if (useFallback) {
      // LocalStorage version
      const newUser = {
        ...formData,
        id: Date.now()
      };
      const updatedUsers = [...users, newUser];
      saveUsersLocalStorage(updatedUsers);
      resetForm();
      setShowAddModal(false);
    } else {
      // API version
      try {
        const result = await api.createUser(formData);
        if (result.success) {
          await loadUsers();
          resetForm();
          setShowAddModal(false);
        } else {
          setError('Failed to create user');
        }
      } catch (error) {
        console.error('Failed to create user:', error);
        setError('Failed to create user');
      }
    }
  };

  const handleEditUser = async () => {
    if (!formData.username || !formData.name) {
      return;
    }

    if (useFallback) {
      // LocalStorage version
      const updatedUsers = users.map(user =>
        user.id === editingUser.id
          ? { ...user, ...formData, password: formData.password || user.password }
          : user
      );
      saveUsersLocalStorage(updatedUsers);
      resetForm();
      setEditingUser(null);
      setShowAddModal(false);
    } else {
      // API version
      try {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if not provided
        }
        const result = await api.updateUser(editingUser.userId || editingUser.id, updateData);
        if (result.success) {
          await loadUsers();
          resetForm();
          setEditingUser(null);
          setShowAddModal(false);
        } else {
          setError('Failed to update user');
        }
      } catch (error) {
        console.error('Failed to update user:', error);
        setError('Failed to update user');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    if (useFallback) {
      // LocalStorage version
      const updatedUsers = users.filter(u => u.id !== user.id);
      saveUsersLocalStorage(updatedUsers);
    } else {
      // API version
      try {
        const result = await api.deleteUser(user.userId || user.id);
        if (result.success) {
          await loadUsers();
        } else {
          setError('Failed to delete user');
        }
      } catch (error) {
        console.error('Failed to delete user:', error);
        setError('Failed to delete user');
      }
    }
  };

  const openAddModal = () => {
    resetForm();
    setEditingUser(null);
    setShowAddModal(true);
  };

  const openEditModal = (user) => {
    setFormData({
      username: user.username,
      password: '',
      name: user.name,
      isAdmin: user.isAdmin
    });
    setEditingUser(user);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      isAdmin: false
    });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingUser) {
      handleEditUser();
    } else {
      handleAddUser();
    }
  };

  return (
    <div className="user-management-overlay" onClick={onClose}>
      <div className="user-management-content" onClick={(e) => e.stopPropagation()}>
        <div className="user-management-header">
          <h2>User Management</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="user-management-body">
          {useFallback && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '15px',
              fontSize: '13px',
              color: '#856404'
            }}>
              <strong>⚠️ Using LocalStorage Mode</strong> - API not configured
            </div>
          )}

          {error && (
            <div style={{
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '15px',
              fontSize: '13px',
              color: '#721c24'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <button className="add-user-button" onClick={openAddModal} disabled={isLoading}>
            + Add User
          </button>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Loading users...
            </div>
          ) : (
            <div className="users-list">
              {users.map(user => (
                <div key={user.id || user.userId} className="user-item">
                  <div className="user-info">
                    <div className="user-name-section">
                      <strong>{user.name}</strong>
                      {user.isAdmin && <span className="admin-badge-small">Admin</span>}
                    </div>
                    <div className="user-username">@{user.username}</div>
                  </div>
                  <div className="user-actions">
                    <button onClick={() => openEditModal(user)} className="edit-button">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteUser(user)} className="delete-button-small">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showAddModal && (
          <div className="add-user-modal">
            <div className="modal-backdrop" onClick={() => setShowAddModal(false)} />
            <div className="modal-box">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="johndoe"
                    required
                    disabled={!!editingUser}
                  />
                </div>

                <div className="form-group">
                  <label>Password {editingUser && '(leave blank to keep current)'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required={!editingUser}
                  />
                </div>

                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isAdmin}
                      onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                    />
                    <span>Admin Privileges</span>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowAddModal(false)} className="cancel-button">
                    Cancel
                  </button>
                  <button type="submit" className="submit-button">
                    {editingUser ? 'Update User' : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
